import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 120000,
});

// Cache de requests pendientes para evitar duplicados
const pendingRequests = new Map();

// Función para generar una clave única para cada request
function getRequestKey(config) {
  const method = config.method?.toUpperCase() || 'GET';
  const url = `${config.baseURL || ''}${config.url || ''}`;
  const params = config.params ? JSON.stringify(config.params) : '';
  const data = config.data ? JSON.stringify(config.data) : '';
  return `${method}:${url}:${params}:${data}`;
}

// Guardar los métodos originales de axios
const originalRequest = api.request.bind(api);

// Envolver el método request para cachear requests duplicados
api.request = function(config) {
  const requestKey = getRequestKey(config);
  
  // Si ya hay un request pendiente con la misma clave, retornar esa promesa
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }
  
  // Crear el request original
  const requestPromise = originalRequest(config)
    .then((response) => {
      // Limpiar del cache cuando se completa
      pendingRequests.delete(requestKey);
      return response;
    })
    .catch((error) => {
      // Limpiar del cache en caso de error
      pendingRequests.delete(requestKey);
      throw error;
    });
  
  // Guardar la promesa en el cache
  pendingRequests.set(requestKey, requestPromise);
  
  return requestPromise;
};

// Interceptor para agregar token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Solo manejar errores HTTP reales (status !== 200-299)
    const status = error.response?.status;
    
    // Manejar errores 401 (No autenticado) - solo si es un error HTTP real
    if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const onLoginPage = window.location.pathname === '/login'
        || window.location.pathname.endsWith('/login');
      if (!onLoginPage) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
    
    // Manejar errores 403 (Prohibido) - solo si es un error HTTP real
    if (status === 403) {
      const msg = String(error.response?.data?.message || '');
      const onLoginPage = window.location.pathname === '/login'
        || window.location.pathname.endsWith('/login');
      // Perfil limitado: 403 operativo no debe cerrar sesión
      const isOperationalForbidden =
        msg.toLowerCase().includes('solo lectura')
        || msg.toLowerCase().includes('configuración del sistema')
        || msg.toLowerCase().includes('configuracion del sistema');
      if (!isOperationalForbidden && !onLoginPage) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

export default api;

